import { Injectable } from '@angular/core'
import { CastServer } from './cast-server'
import { LogService } from 'htv-lib'
import { catchError, EMPTY, map, Observable, of } from 'rxjs'
import { GoogleCastProxyServerState } from '../interfaces/local-googlecast'
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http'
import { GoogleCastConfig } from '../interfaces/htv-config'
import { NetworkService } from './network.service'

@Injectable({
  providedIn: 'root',
})
export class CastIQCast implements CastServer {
  private readonly statusUrl = 'http://qcast-iptv2.sensiq/php-scripts/qcastcode.php'

  macAddress?: string

  constructor(
    private httpClient: HttpClient,
    private logService: LogService,
    private networkService: NetworkService
  ) {
    // do nothing
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  init(config?: GoogleCastConfig): void {
    this.logService.info('[CastIQCast] Init')

    this.networkService.networkData$.subscribe(networkData => {
      this.macAddress = networkData?.macAddress
    })
  }

  queryProxyStatus(): Observable<GoogleCastProxyServerState | undefined> {
    if (!this.macAddress) {
      this.logService.error(`[CastIQCast] Invalid mac address, aborting IQCast proxy query`)
      return of(undefined)
    }

    const params = new HttpParams().set('field', 'macaddress').set('value', this.macAddress).set('format', 'json')

    this.logService.debug(`[CastIQCast] (status) request: ${this.statusUrl}?${params.toString()}`)

    return this.httpClient.get(this.statusUrl, { params }).pipe(
      map((res: any) => {
        this.logService.debug(`[CastIQCast] (status) response: ${this.safeStringify(res)}`)
        if (res) {
          if (res.records && res.records.length > 0) {
            const record = res.records[0]
            const proxyUrl = res?.qcastproxyurl ?? ''
            const code = record?.code ?? ''
            return {
              hasError: false,
              pairingStatus: record?.paired == '1',
              code: code,
              qrcodeUrl: `${proxyUrl}${code}`,
              registerUrl: res?.qcastproxyaddress ?? '',
              wifiSSID: res?.guestwifissid ?? '',
            } as GoogleCastProxyServerState
          } else {
            this.logService.error(`[CastIQCast] (status) Received no record from IQCast server`)
            return {
              hasError: true,
              pairingStatus: false,
              errorKey: 'NO_RECORD',
            } as GoogleCastProxyServerState
          }
        } else {
          this.logService.error(`[CastIQCast] (status) Received empty response from IQCast server`)
          return {
            hasError: true,
            pairingStatus: false,
            errorKey: 'EMPTY_RESPONSE',
          } as GoogleCastProxyServerState
        }
      }),
      catchError((err: HttpErrorResponse) => {
        const state: GoogleCastProxyServerState = {
          hasError: true,
          pairingStatus: false,
        }
        switch (err.status) {
          case 0:
            state.errorKey = 'SERVER_UNREACHABLE'
            break
          case 404:
            state.errorKey = 'NOT_FOUND'
            break
          case 500:
            state.errorKey = 'SERVER_ERROR'
            break
          default:
            state.errorKey = undefined
            break
        }
        this.logService.error(
          `[CastIQCast] Could not retrieve GoogleCast proxy status, error = ${err?.message} (${state.errorKey})`
        )
        return of(state)
      })
    )
  }

  // There is no checkout with IQCast since the logic is that every time you request the Cast QRCode page the pair state is cleared
  sendCheckoutToProxy(): Observable<void> {
    return EMPTY
  }

  safeStringify(value: unknown): string | undefined {
    try {
      return JSON.stringify(value) ?? undefined
    } catch {
      return undefined
    }
  }
}
